import React, { useEffect, useRef, useState } from 'react';

export interface IFilterOption {
  label: string;
  value: string;
}

/**
 * A filter of a report as a dropdown: a click chooses one choice and closes the list, a
 * click with Ctrl (⌘ on a Mac) adds the choice to the chosen ones or takes it away and keeps
 * the list open. Nothing chosen is all.
 */
const MultiSelectFilter = ({
  allLabel,
  chosenLabel,
  label,
  onChange,
  options,
  value,
}: {
  // The text when nothing is chosen, e.g. "Kaikki osastot"
  allLabel: string;
  // The text of many chosen, e.g. "3 valittu"
  chosenLabel: (count: number) => string;
  label: string;
  onChange: (value: string[]) => void;
  options: IFilterOption[];
  value: string[];
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Closed by a click outside of it or by Escape
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOutside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const choose = (option: string, addToChosen: boolean) => {
    if (addToChosen) {
      onChange(
        value.includes(option)
          ? value.filter((chosen) => chosen !== option)
          : [...value, option]
      );
      return;
    }

    onChange([option]);
    setOpen(false);
  };

  let text = allLabel;
  if (value.length === 1) {
    text =
      options.find((option) => option.value === value[0])?.label ?? value[0];
  } else if (value.length > 1) {
    text = chosenLabel(value.length);
  }

  return (
    <div className="report-filter" ref={container}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        className={`report-filter-button${value.length > 0 ? ' chosen' : ''}`}
        onClick={() => setOpen(!open)}
        title={label}
        type="button"
      >
        <span className="report-filter-text">{text}</span>
        <span aria-hidden className="report-filter-arrow">
          ▾
        </span>
      </button>
      {open ? (
        <div
          aria-label={label}
          aria-multiselectable
          className="report-filter-list"
          role="listbox"
        >
          <button
            aria-selected={value.length === 0}
            className={value.length === 0 ? 'chosen' : ''}
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
            role="option"
            type="button"
          >
            {allLabel}
          </button>
          {options.map((option) => (
            <button
              aria-selected={value.includes(option.value)}
              className={value.includes(option.value) ? 'chosen' : ''}
              key={option.value}
              onClick={(event) =>
                choose(option.value, event.ctrlKey || event.metaKey)
              }
              role="option"
              type="button"
            >
              <span aria-hidden className="report-filter-check">
                {value.includes(option.value) ? '✓' : ''}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MultiSelectFilter;
